import { useEffect, useSyncExternalStore } from 'react';

// One-at-a-time queue for popups and banners that appear on their own
// (splash, agreement, no-show warning, bonus celebration, email/PWA banners).
//
// Each overlay registers while it *wants* to show. A modal is displayed only
// if it has the highest priority among wanting modals; a banner only if no
// modal wants to show and it has the highest priority among wanting banners.
// Everything else waits its turn and appears once the one above is closed.

interface Want { prio: number; modal: boolean }

const wants = new Map<string, Want>();
const listeners = new Set<() => void>();
let version = 0;

function emit() {
  version++;
  listeners.forEach(l => l());
}
function subscribe(l: () => void) {
  listeners.add(l);
  return () => { listeners.delete(l); };
}
const getVersion = () => version;

function isShown(id: string): boolean {
  const me = wants.get(id);
  if (!me) return false;
  for (const [otherId, w] of wants) {
    if (otherId === id) continue;
    if (me.modal) {
      if (w.modal && w.prio > me.prio) return false;
    } else {
      if (w.modal) return false;
      if (w.prio > me.prio) return false;
    }
  }
  return true;
}

export const OVERLAY_PRIO = {
  agreement: 100,
  splash: 99,
  welcome: 98,
  noShow: 80,
  loyaltyCelebration: 70,
  emailBanner: 20,
  pwaBanner: 10,
} as const;

/** Register a wish to show; returns whether this overlay should render now. */
export function useOverlaySlot(id: string, want: boolean, prio: number, modal: boolean): boolean {
  useSyncExternalStore(subscribe, getVersion, getVersion);
  useEffect(() => {
    if (!want) return;
    wants.set(id, { prio, modal });
    emit();
    return () => { wants.delete(id); emit(); };
  }, [id, want, prio, modal]);
  return want && isShown(id);
}

/** True while any modal wants the screen (used to hide persistent bars). */
export function useAnyModalOpen(): boolean {
  useSyncExternalStore(subscribe, getVersion, getVersion);
  for (const w of wants.values()) if (w.modal) return true;
  return false;
}
